<template>
  <tr
    class="bg-gray-50 hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-16 last-child:pr-3 text-sm"
    :class="
      {
        'bg-gray-50 border-gray-100': (depth || 0) <= 1,
        'bg-gray-200 border-gray-300': depth === 2,
        'bg-gray-300 border-gray-400': depth === 3,
        'bg-gray-400 border-gray-500': (depth || 0) >= 4
      }
    "
  >
    <td
      class="py-3 text-left pl-3"
    >
      <div
        class="flex items-center space-x-2"
        :style="{ paddingLeft: `${((depth || 0) * 20) + 16}px` }"
      >
        <input
          v-if="tradeItem.budget > 0 && ($props.claim.status === ClaimStatus.draft_pqs || $props.claim.status === ClaimStatus.draft_contractor)"
          v-model="selectTradeItem"
          type="checkbox"
          name="select_trade_item[]"
          class="form-checkbox"
          :aria-label="$t('ClaimCertifiedCosts.TradeRowItem.accessibility.selectTradeItem')"
        >

        <span class="font-semibold text-gray-900">{{ $props.tradeItem.billNumber }}</span>

        <ProvisionalSumBadge
          v-if="!tradeItem.has_children && tradeItem.is_provisional_sum"
        />

        <p class="w-60 text-gray-700">
          {{ tradeItem.name }}
        </p>
      </div>
    </td>

    <td
      class="pr-3"
      :title="showBudgetCommentTooltip ? varianceComment.body : ''"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showBudgetCommentButton"
          type="button"
          class="btn-icon btn-icon-default mr-1 shrink-0"
          :aria-label="varianceComment ? $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editComment') : $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.addComment')"
          @click.stop="editBudgetComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <button
          v-else-if="!!varianceComment?.body"
          type="button"
          class="btn-icon btn-icon-default mr-1 shrink-0"
          @click.stop="showBudgetComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ australianCurrency(tradeItem.budget) }}
        </div>
      </div>
    </td>

    <td
      v-if="tradeItemCost && !typingClaim"
      :class="{ 'cursor-pointer': canEditClaim }"
      class="bg-green-200/50"
      @click="typeClaim"
    >
      <div class="group flex items-center justify-end">
        <button
          v-if="canEditClaim"
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        {{ canEditClaim ? $tcc(costForm.form.current_claim) : $tcc(tradeItemCost.current_claim) }}
      </div>
    </td>

    <td
      v-else-if="canEditClaim && hasValidBudget"
      class="bg-green-200/50"
    >
      <div class="">
        <AppInput
          ref="claimInput"
          v-model.number="costForm.form.current_claim"
          name="current_claim"
          type="number"
          min="1"
          data-test="quantity-input"
          class="w-20 h-6 ml-auto form-input"
          :aria-label="$t('ClaimCertifiedCosts.TradeRowItem.accessibility.currentClaim')"
          @blur="stopTypingClaim"
          @keyup.enter="$event.target.blur()"
          @keyup.esc="typingClaim = false"
        />
      </div>
    </td>

    <td
      v-else
      class="bg-green-200/50"
    />

    <td
      v-if="tradeItemCost?.current_certified && !typingCertified"
      :class="{ 'cursor-pointer': canEditCertified }"
      class="bg-green-200/50"
      @click="typeCertified"
    >
      <div class="group flex items-center justify-end">
        <button
          v-if="canEditCertified"
          type="button"
          class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </button>
        {{ tradeItemCost.current_certified ? $tcc(tradeItemCost.current_certified) : '' }}
      </div>
    </td>

    <td
      v-else-if="canEditCertified && hasValidBudget"
      class="bg-green-200/50"
    >
      <div class="">
        <AppInput
          ref="certifiedInput"
          v-model.number="costForm.form.current_certified"
          name="current_certified"
          type="number"
          min="1"
          class="w-20 h-6 ml-auto form-input"
          :aria-label="$t('ClaimCertifiedCosts.TradeRowItem.accessibility.currentCertified')"
          @blur="stopTypingCertified"
          @keyup.enter="$event.target.blur()"
          @keyup.esc="typingCertified = false"
        />
      </div>
    </td>

    <td
      v-else
      class="bg-green-200/50"
    />

    <td
      class="group bg-green-200/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 && varianceComment?.body ? varianceComment.body : ''"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.editComment')"
          @click.stop="editVarianceComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ hasVariance ? $tcc(variance) : '' }}
        </div>
      </div>
    </td>

    <td class="bg-green-200/50">
      {{ hasValidBudget ? $tcc(tradeItem.previous(claim)) : '' }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ hasValidBudget ? $tcc(tradeItem.previous(claim) + (tradeItemCost?.current_certified || 0)) : '' }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ tradeItemCost ? $tp((tradeItemCost.current_claim / tradeItem.budget) * 100) : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ tradeItemCost?.current_certified ? $tp((tradeItemCost.current_certified / tradeItem.budget) * 100) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ hasValidBudget ? $tp((tradeItem.previous(claim) / tradeItem.budget) * 100) : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ hasValidBudget ? $tp(((tradeItem.previous(claim) + (tradeItemCost?.current_certified || 0)) / tradeItem.budget) * 100) : '' }}
    </td>

    <td class="pr-3">
      {{ hasValidBudget ? $tcc(Math.abs(tradeItem.budget - tradeItem.previous(claim) - (tradeItemCost?.current_certified || 0))) : '' }}
    </td>
  </tr>
</template>

<script setup lang="ts">
import ProvisionalSumBadge from '@/components/shared/ProvisionalSumBadge.vue';
import { useFormData } from '@/composables/useFormData';
import { useSignedData } from '@/composables/useSignedData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, falsey, parseUrlParams } from '@/helpers';
import { t } from '@/i18n';
import Claim, { ClaimStatus } from '@/models/Claim';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useCommentsStore } from '@/store/comments';
import { useTradeItemCostsStore } from '@/store/tradeItemCosts';
import { useTradeItemsStore } from '@/store/tradeItems';
import Swal from 'sweetalert2';
import { computed, nextTick, ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  trade: Trade;
  claim: Claim;
  tradeItem: TradeItem;
  depth?: number;
  showPercentages?: boolean;
}>();

const tradeItemsStore = useTradeItemsStore();
const tradeItemCostsStore = useTradeItemCostsStore();

const tradeItemCost = computed(() => {
  return tradeItemCostsStore.models.with('comments', (query) => query.orderBy('created_at', 'desc')).where(
    'trade_item_id',
    props.tradeItem.id,
  ).where(
    'claim_id',
    props.claim.id,
  )
    .first();
});

const varianceComment = computed(() => {
  return tradeItemCost.value?.comments[0];
});

const variance = computed(() => {
  return tradeItemCost.value?.current_claim - tradeItemCost.value?.current_certified;
});

const hasVariance = computed(() => {
  return !falsey(tradeItemCost.value?.current_claim) && !falsey(tradeItemCost.value?.current_certified);
});

const hasValidBudget = computed(() => {
  return props.tradeItem.budget > 0;
});

const typingClaim = ref(false);
const typingCertified = ref(false);
const signedParams = useSignedData();

const canEditClaim = computed(() => {
  return signedParams.meta.edit_claim &&
    (props.claim.status === ClaimStatus.contractor_claim || props.claim.status === ClaimStatus.draft_contractor);
});

const canEditCertified = computed(() => {
  return signedParams.meta.edit_certified && props.claim.status === ClaimStatus.pqs_review;
});

const claimInput = ref();
const certifiedInput = ref();

const typeClaim = () => {
  if(canEditClaim.value) {
    typingClaim.value = true;

    nextTick(() => {
      claimInput.value?.focusInput();
    });
  }
};

const typeCertified = () => {
  if(canEditCertified.value) {
    typingCertified.value = true;

    nextTick(() => {
      certifiedInput.value?.focusInput();
    });
  }
};

const costForm = useFormData({
  id: undefined,
  current_certified: 0,
  current_claim: 0,
  comment_trade_item: false,
  comment: undefined,
  trade_item_id: undefined,
});

costForm.setData({
  trade_item_id: props.tradeItem.id,
});

watch(tradeItemCost, (newValue) => {
  if(newValue) {
    if(canEditCertified.value) {
      costForm.setData({
        id: newValue.id,
        current_certified: newValue.current_certified || 0,
      });
    } else if(canEditClaim.value) {
      costForm.setData({
        id: newValue.id,
        current_claim: newValue.current_claim || 0,
      });
    }
  }
}, { immediate: true });

const selectTradeItem = computed({
  get() {
    return tradeItemsStore.selectedTradeItems.has(props.tradeItem.id);
  },

  set(value) {
    if(value) {
      tradeItemsStore.selectedTradeItems.add(props.tradeItem.id);
    } else {
      tradeItemsStore.selectedTradeItems.delete(props.tradeItem.id);
    }
  },
});

const storeSingleCertifiedAction = useStoreApiAction(tradeItemCostsStore.saveCertifiedCost);
const storeSingleClaimAction = useStoreApiAction(tradeItemCostsStore.saveClaimCost);
const storeSignedCommentAction = useStoreApiAction(tradeItemCostsStore.storeSignedComment);
const updateSignedCommentAction = useStoreApiAction(tradeItemCostsStore.updateSignedComment);

const storeCommentParams = computed(() => {
  return parseUrlParams(signedParams.meta.store_comment_url as string);
});

const updateCommentParams = computed(() => {
  return parseUrlParams(signedParams.meta.update_comment_url as string);
});

const formParams = computed(() => {
  return parseUrlParams(signedParams.meta.url as string);
});

const showCommentButton = computed(() => {
  return hasVariance.value && variance.value !== 0 && !!varianceComment.value && canEditCertified.value;
});

const showBudgetCommentButton = computed(() => {
  return canEditCertified.value &&
    !falsey(tradeItemCost.value) &&
    !falsey(tradeItemCost.value.current_certified) &&
    !falsey(tradeItemCost.value.current_claim);
});

const showBudgetCommentTooltip = computed(() => {
  return !!varianceComment.value?.body && showBudgetCommentButton.value;
});

const commentsStore = useCommentsStore();

const saveSignedComment = (comment: string) => {
  return storeSignedCommentAction.request(props.claim.id, storeCommentParams.value, {
    trade_item_cost_id: tradeItemCost.value!.id,
    comment,
  });
};

const saveSignedCommentUpdate = (comment: string) => {
  return updateSignedCommentAction.request(props.claim.id, updateCommentParams.value, {
    trade_item_cost_id: tradeItemCost.value!.id,
    comment,
  });
};

const showBudgetComment = () => {
  Swal.fire({
    icon: 'info',
    title: (hasVariance.value && variance.value !== 0) ? 'Reason for Adjustment' : 'Comment',
    text: varianceComment.value?.body,
  });
};

const editBudgetComment = () => {
  Swal.fire({
    icon: 'info',
    title: (hasVariance.value && variance.value !== 0) ? 'Reason for Adjustment' : 'Comment',
    text: (hasVariance.value && variance.value !== 0) ?
      'Please provide a reason for the variance between the claim and certified amounts.' :
      'Provide an optional comment for the cost.',

    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: varianceComment.value ? 'Update' : 'Save',
  }).then((result) => {
    if(result.isConfirmed && result.value) {
      if(varianceComment.value) {
        updateSignedCommentAction.request(props.claim.id, updateCommentParams.value, {
          trade_item_cost_id: tradeItemCost.value!.id,
          comment: result.value,
        })
          .then(() => {
            toast.success('Comment updated');
          });
      } else {
        storeSignedCommentAction.request(props.claim.id, storeCommentParams.value, {
          trade_item_cost_id: tradeItemCost.value!.id,
          comment: result.value,
        }).then(() => {
          toast.success('Comment saved');
        });
      }
    }
  });
};

const editVarianceComment = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentTitle'),
    text: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: 'Update',
  }).then((result) => {
    if(result.isConfirmed && result.value) {
      saveSignedCommentUpdate(result.value).catch(() => {
        Swal.fire({
          icon: 'error',
          title: t('ClaimCertifiedCosts.TradeRowItem.alert.errorTitle'),
        });
      });
    }
  });
};

const stopTypingCertified = async () => {
  if(costForm.form.current_certified !== tradeItemCost.value.current_certified) {
    if(costForm.form.current_certified !== tradeItemCost.value.current_claim) {
      const tradeComment = commentsStore.models
        .where('commentable_id', props.trade.id)
        .where('commentable_type', props.trade.$entity())
        .where('creator_id', props.claim.id)
        .where('creator_type', props.claim.$entity())
        .first();

      const comment = !tradeComment ?
        await Swal.fire({
          icon: 'warning',
          title: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentTitle'),
          text: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentText'),
          input: 'text',
        }) :
        undefined;

      costForm.form.comment = comment?.value || tradeComment?.body;

      // TODO: 10/27/23 maybe stop prompting to apply to trade

      if(!tradeComment) {
        await Swal.fire({
          icon: 'question',
          title: t('ClaimCertifiedCosts.TradeRowItem.alert.applyCommentTitle'),
          showConfirmButton: true,
          confirmButtonText: t('ClaimCertifiedCosts.TradeRowItem.alert.confirmYes'),
          showDenyButton: true,
        }).then((result) => {
          if(result.isConfirmed) {
            costForm.form.comment_trade_item = true;
          }
        });
      }

      storeSingleCertifiedAction.request(props.claim.id, formParams.value, costForm.form).then((tradeItemCost) => {
        console.log(tradeItemCost);
      }).catch((error) => {
        console.log(error);

        if(error?.status === 422) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid amount.',
            text: error.data.message,
          });
        }

        costForm.form.current_certified = tradeItemCost.value.current_certified;
      });
    } else {
      costForm.form.comment = undefined;

      storeSingleCertifiedAction.request(props.claim.id, formParams.value, costForm.form).then((tradeItemCost) => {
        console.log(tradeItemCost);
      }).catch((error) => {
        console.log(error);

        if(error?.status === 422) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid amount.',
            text: error.data.message,
          });
        }

        costForm.form.current_certified = tradeItemCost.value.current_certified;
      });
    }
  }

  typingCertified.value = false;
};

const stopTypingClaim = () => {
  if(!falsey(costForm.form.current_claim) && costForm.form.current_claim !== tradeItemCost.value?.current_claim) {
    storeSingleClaimAction.request(props.claim.id, formParams.value, costForm.form).then((tradeItemCost) => {
      console.log(tradeItemCost);
    }).catch((error) => {
      if(error?.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid amount.',
          text: error.data.message,
        });
      }
    });
  }

  typingClaim.value = false;
};
</script>

<style scoped></style>
